import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { io } from "socket.io-client";
import identityService from "../../api/identityService";
import { API_CONFIG } from "../../utils/constants";
import { ROLE_LABELS } from "../../components/navConfig";

function when(value) {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function chrono(value, now) {
  if (!value) return "00:00.0";
  const total = Math.max(0, now - new Date(value).getTime());
  const hours = Math.floor(total / 3600000);
  const minutes = Math.floor((total % 3600000) / 60000);
  const seconds = Math.floor((total % 60000) / 1000);
  const tenths = Math.floor((total % 1000) / 100);
  const clock = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${tenths}`;
  return hours ? `${hours}:${clock}` : clock;
}

function age(value, now) {
  if (!value) return "—";
  const ms = Math.max(0, now - new Date(value).getTime());
  if (ms < 1000) return "ahora";
  return chrono(value, now);
}

export default function LiveActivity() {
  const [users, setUsers] = useState([]);
  const [feed, setFeed] = useState([]);
  const [connected, setConnected] = useState(false);
  const [clock, setClock] = useState(Date.now());
  const skewRef = useRef(0);

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now() - skewRef.current), 100);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let socket;
    let cancelled = false;

    identityService.live()
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || {};
        if (data.serverNow) skewRef.current = Date.now() - data.serverNow;
        setUsers(data.users || []);
        setFeed(data.feed || []);
      })
      .catch(() => {});

    const connect = () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      socket = io(`${API_CONFIG.SOCKET_URL}/presence`, {
        auth: { token },
        transports: ["websocket", "polling"],
      });

      socket.on("connect", () => setConnected(true));
      socket.on("disconnect", () => setConnected(false));
      const align = (data) => {
        if (data?.serverNow) skewRef.current = Date.now() - data.serverNow;
      };
      socket.on("live:snapshot", (data) => {
        align(data);
        setUsers(data?.users || []);
        setFeed(data?.feed || []);
      });
      socket.on("live:update", (data) => {
        align(data);
        setUsers(data?.users || []);
        if (data?.event) {
          setFeed((current) => [data.event, ...current].slice(0, 80));
        }
      });
    };

    connect();
    const onToken = () => {
      socket?.disconnect();
      connect();
    };
    window.addEventListener("bremar-token", onToken);

    return () => {
      cancelled = true;
      window.removeEventListener("bremar-token", onToken);
      socket?.disconnect();
    };
  }, []);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "340px 1fr" }, gap: 1.5, minHeight: 420 }}>
      <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.25, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontWeight: 800 }}>En la aplicación</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: connected ? "success.main" : "text.secondary" }}>
            {connected ? "canal abierto" : "reconectando"}
          </Typography>
        </Box>
        {users.length === 0 && (
          <Typography sx={{ px: 2, pb: 2 }} color="text.secondary">
            Nadie tiene la aplicación abierta.
          </Typography>
        )}
        {users.map((person) => (
          <Box key={person.userId} sx={{ px: 2, py: 1.1, borderTop: "1px solid #f3f4f6" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography sx={{ fontWeight: 800 }}>{person.name}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 800, color: person.focused ? "success.main" : "warning.main" }}>
                {person.focused ? "al frente" : "en segundo plano"}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {ROLE_LABELS[person.role] || person.role || "—"} · {person.page || person.path || "—"}
            </Typography>
            <Typography variant="body2">
              {person.lastAction?.label || "Sin acción todavía"} · {age(person.lastAction?.at || person.lastSeen, clock)}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "ui-monospace, monospace", fontWeight: 700 }}>
              página {chrono(person.pageSince || person.since, clock)}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: "ui-monospace, monospace" }} color="text.secondary">
              sesión {chrono(person.since, clock)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontWeight: 800 }}>Actividad</Typography>
          <Typography variant="caption" color="text.secondary">
            Páginas y acciones. Un token vigente no cuenta como presencia.
          </Typography>
        </Box>
        {feed.length === 0 && (
          <Typography sx={{ px: 2, pb: 2 }} color="text.secondary">Todavía no hay movimientos.</Typography>
        )}
        {feed.map((item, index) => (
          <Box
            key={item.id || `${item.userId}-${item.at}-${index}`}
            sx={{
              px: 2,
              py: 0.9,
              display: "grid",
              gridTemplateColumns: "72px 140px 1fr",
              gap: 1,
              borderTop: "1px solid #f3f4f6",
            }}
          >
            <Typography variant="caption" sx={{ fontFamily: "ui-monospace, monospace" }} color="text.secondary">
              {when(item.at)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {item.name || item.userName || "Usuario"}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {item.label || item.path}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
